'use client';

import { Ref, useCallback, useEffect } from 'react';

import { css } from '@emotion/react';
import { Box, BoxProps } from '@mui/system';
import { areStatesEqual } from '@remirror/core-utils';
import { LinkExtension } from '@remirror/extension-link';
import { PlaceholderExtension } from '@remirror/extension-placeholder';
import { Remirror, useRemirror, useRemirrorContext } from '@remirror/react';
import { RichTextContent } from '@scorenco/core';

const TextEditor = ({ ref, ...rest }: BoxProps) => {
  const { getRootProps } = useRemirrorContext();

  // `key` est extrait : React interdit de le passer via un spread.
  // eslint-disable-next-line react-hooks/refs
  const { key, ...rootProps } = getRootProps({ ref: ref as Ref<HTMLElement> });

  return (
    <Box
      css={(theme) => css`
        .remirror-editor {
          padding: 0.5rem 1rem;
          border: 1px solid ${theme.vars.palette.divider};
          border-radius: 1.75rem;
          outline: 0;
          max-height: 6rem;
          overflow: auto;
          overflow-y: auto !important;
          background-color: ${theme.vars.palette.background.paper};

          &:focus {
            box-shadow: 0 0 0 0.2em
              rgba(${theme.vars.palette.primary.mainChannel} / 0.12);
            border-color: ${theme.vars.palette.primary.light};
          }

          p {
            margin: 0;
          }
        }
      `}
      {...rootProps}
      {...rest}
    />
  );
};

export type RichTextInputProps = BoxProps & {
  value?: RichTextContent;
  disabled?: boolean;
  placeholder?: string;
  onReturn?: (value?: RichTextContent) => void;
  onChange?: (value?: RichTextContent) => void;
};

export const RichTextInput = ({
  value,
  onChange,
  placeholder,
  disabled,
  // onReturn n'est pas utilisé ici mais ne doit pas fuir vers le DOM
  onReturn,
  ...rest
}: RichTextInputProps) => {
  const extensions = useCallback(
    () => [
      new PlaceholderExtension({ placeholder }),
      new LinkExtension({ autoLink: true, defaultTarget: '_blank' }),
    ],
    [placeholder]
  );
  const remirror = useRemirror({
    extensions,
    stringHandler: 'html',
    content: value,
  });

  useEffect(() => {
    if (JSON.stringify(remirror.state.toJSON().doc) !== JSON.stringify(value)) {
      remirror.manager.view.updateState(
        remirror.manager.createState({ content: value })
      );
    }
  }, [value]);

  return (
    <Remirror
      manager={remirror.manager}
      state={remirror.state}
      onChange={(parameter) => {
        remirror.onChange(parameter);

        if (
          !areStatesEqual(parameter.state, parameter.previousState) &&
          onChange
        ) {
          const { doc } = parameter.state.toJSON();
          onChange(doc);
        }
      }}
      editable={disabled}
    >
      <TextEditor {...rest} />
    </Remirror>
  );
};
